export const GOAL_STATUS = {
  DRAFT: "Draft",
  SUBMITTED: "Submitted",
  MANAGER_REVIEW: "Manager Review",
  CHANGES_REQUESTED: "Changes Requested",
  APPROVED: "Approved",
  IN_PROGRESS: "In Progress",
  COMPLETED: "Completed",
  CLOSED: "Closed",
};

export const GOAL_STATUS_FLOW = [
  GOAL_STATUS.DRAFT,
  GOAL_STATUS.SUBMITTED,
  GOAL_STATUS.MANAGER_REVIEW,
  GOAL_STATUS.APPROVED,
  GOAL_STATUS.IN_PROGRESS,
  GOAL_STATUS.COMPLETED,
  GOAL_STATUS.CLOSED,
];

export const EDITABLE_GOAL_STATUSES = [GOAL_STATUS.DRAFT, GOAL_STATUS.CHANGES_REQUESTED];
export const APPROVED_GOAL_STATUSES = [
  GOAL_STATUS.APPROVED,
  GOAL_STATUS.IN_PROGRESS,
  GOAL_STATUS.COMPLETED,
  GOAL_STATUS.CLOSED,
];

export const SHEET_STATUS = {
  NOT_STARTED: "Not Started",
  DRAFT: "Draft",
  PENDING: "Pending Approval",
  CHANGES_REQUESTED: "Changes Requested",
  APPROVED: "Approved",
};

export const ASSESSMENT_STATUS = {
  NOT_STARTED: "Not Started",
  DRAFT: "Draft",
  SUBMITTED: "Submitted",
};

export const RESULT_STATUS = {
  NOT_READY: "Awaiting Assessments",
  PENDING_HR: "Pending HR Review",
  FINALIZED: "Finalized",
};

export const PRIORITIES = ["High", "Medium", "Low"];
// [UPDATED] Goals, KRAs and KPIs are measured on a scale the person sets (from / to), 0 to 5 by default.
// The target must lie on that scale. The scale is saved as the measurement unit, e.g. "Scale (0-5)" or "Scale (1-10)".
export const MEASUREMENT_SCALE = { unit: "Scale (0-5)", label: "0 to 5", min: 0, max: 5, step: 0.5, defaultTarget: 5 };
const fmtNum = (n) => String(Number(n));
export const scaleUnit = (min, max) => `Scale (${fmtNum(min)}-${fmtNum(max)})`;
export function parseScaleUnit(unit) {
  const m = /^Scale \((\d+(?:\.\d+)?)-(\d+(?:\.\d+)?)\)$/.exec(String(unit || "").trim());
  return m ? { min: Number(m[1]), max: Number(m[2]) } : null;
}
// [NEW] One text box for the scale: "0 to 5", "1-10", "0 – 100" or just "5" (= 0 to 5).
export const scaleText = (min, max) => `${fmtNum(min)} to ${fmtNum(max)}`;
export function parseScaleText(text) {
  const t = String(text || "").trim().toLowerCase();
  let m = /^(\d+(?:\.\d+)?)$/.exec(t);
  if (m) return { min: 0, max: Number(m[1]) };
  m = /^(\d+(?:\.\d+)?)\s*(?:to|-|–|—)\s*(\d+(?:\.\d+)?)$/.exec(t);
  return m ? { min: Number(m[1]), max: Number(m[2]) } : null;
}
export const scaleTextFromUnit = (unit) => { const sc = parseScaleUnit(unit); return sc ? scaleText(sc.min, sc.max) : MEASUREMENT_SCALE.label; };
export function scaleTextError(text) {
  const sc = parseScaleText(text);
  if (!sc) return 'Write the scale like "0 to 5" or "1 to 10".';
  return scaleError(sc.min, sc.max, null, { needTarget: false });
}
export const unitFromScaleText = (text) => { const sc = parseScaleText(text) || { min: MEASUREMENT_SCALE.min, max: MEASUREMENT_SCALE.max }; return scaleUnit(sc.min, sc.max); };

// Error text for a scale + target, or "" when they are fine.
export function scaleError(min, max, target, { needTarget = true } = {}) {
  const lo = Number(min);
  const hi = Number(max);
  if (min === "" || max === "" || Number.isNaN(lo) || Number.isNaN(hi)) return "Enter where the scale starts and ends.";
  if (lo < 0) return "The scale cannot start below 0.";
  if (hi <= lo) return "The end of the scale must be higher than its start.";
  if (hi > 1000000) return "The scale end is too large.";
  if (!needTarget) return "";
  const t = Number(target);
  if (target === "" || target === null || target === undefined || Number.isNaN(t)) return `Enter a target between ${fmtNum(lo)} and ${fmtNum(hi)}.`;
  if (t <= lo || t > hi) return `The target must be on the scale: above ${fmtNum(lo)} and at most ${fmtNum(hi)}.`;
  return "";
}

export const MEASUREMENT_UNITS = ["%", "Count", "Amount (₹)", "Amount (₹ lakh)", "Hours", "Days", "Milliseconds", "Score"];

export const KPI_FREQUENCIES = ["Once", "Monthly", "Quarterly", "Half-yearly", "Annual"];

export const KPI_DIRECTIONS = [
  { value: "higher", label: "Higher is better" },
  { value: "lower", label: "Lower is better" },
  { value: "target", label: "Target based" },
];

export const EMPLOYMENT_STATUSES = ["Active", "Inactive", "Exited"];

export const STAGES = {
  GOAL_SETTING: "goalSetting",
  MID_YEAR: "midYear",
  SELF_ASSESSMENT: "selfAssessment",
  MANAGER_REVIEW: "managerReview",
  HR_FINALIZATION: "hrFinalization",
};

export const STAGE_LABELS = {
  goalSetting: "Goal setting",
  midYear: "Mid-year review",
  selfAssessment: "Self assessment",
  managerReview: "Manager review",
  hrFinalization: "HR finalization",
};

export const STAGE_ORDER = ["goalSetting", "midYear", "selfAssessment", "managerReview", "hrFinalization"];

export const GOAL_COLORS = ["#5B4FE0", "#3AA0E8", "#2BB5A6", "#F2A33A", "#E0679B", "#8B7FF0", "#6C7A96", "#12A57A"];

export const GOAL_SOURCES = {
  EMPLOYEE: "Employee proposed",
  MANAGER: "Manager assigned",
  ORG: "Organisation cascaded",
  TEMPLATE: "Role template",
};

export const WEIGHT_OWNERS = {
  EMPLOYEE: "Employee",
  MANAGER: "Manager",
  HR: "HR",
};

// [UPDATED] PM-17: the employee adds their own goals, KRAs and KPIs; the manager approves, reviews and can correct
// them with a reason; nobody assigns them to someone else.
export const OWNERSHIP_RULES = [
  {
    stage: "Goals",
    employee: "Adds own goals with target, due date and weight",
    manager: "Reviews; can correct a goal with a reason while approving",
    decidedBy: WEIGHT_OWNERS.EMPLOYEE,
  },
  {
    stage: "KRAs (daily tasks) & KPIs",
    employee: "Adds own KRAs and KPIs under My KRA & KPI",
    manager: "Approves or rejects them under KRA & KPI approvals",
    decidedBy: WEIGHT_OWNERS.EMPLOYEE,
  },
  {
    stage: "Weight allocation",
    employee: "Makes the goals total exactly 100% and submits the sheet",
    manager: "Approves, or sends back with Request changes and comments",
    decidedBy: WEIGHT_OWNERS.MANAGER,
  },
  {
    stage: "After approval",
    employee: "Updates progress and evidence; can raise a change request",
    manager: "Approves or rejects change requests (approval reopens the sheet)",
    decidedBy: WEIGHT_OWNERS.MANAGER,
  },
];

export const COMPETENCY_TYPES = ["Core", "Functional", "Leadership"];

export const COMPETENCY_LEVELS = [
  { value: 1, label: "Needs development", hint: "Needs close guidance to apply this" },
  { value: 2, label: "Developing", hint: "Applies it with support" },
  { value: 3, label: "Proficient", hint: "Applies it independently, meets the role bar" },
  { value: 4, label: "Advanced", hint: "Applies it in complex situations and coaches others" },
  { value: 5, label: "Role model", hint: "Sets the standard for the organisation" },
];

export const COMPETENCY_STATUS = {
  NOT_STARTED: "Not Started",
  SELF_DONE: "Self Rated",
  MANAGER_DONE: "Manager Rated",
  AGREED: "Agreed",
};

export const PIP_STATUS = {
  DRAFT: "Draft",
  ACTIVE: "Active",
  EXTENDED: "Extended",
  SUCCESSFUL: "Successful",
  UNSUCCESSFUL: "Unsuccessful",
  PARTIAL: "Partially met",
  WITHDRAWN: "Withdrawn",
};

export const PIP_TRIGGERS = [
  "Rating below expectations",
  "Missed KRA / KPI targets",
  "Repeated goal slippage",
  "Behaviour / competency gap",
  "Manager recommendation",
];

export const PIP_DURATIONS = [30, 60, 90];

export const PIP_MILESTONE_STATUS = {
  PENDING: "Pending",
  ON_TRACK: "On Track",
  AT_RISK: "At Risk",
  MET: "Met",
  MISSED: "Missed",
};

export const PIP_OUTCOMES = [
  { value: "Successful", label: "Successful, close the plan", tone: "success" },
  { value: "Extended", label: "Extend by one more review period", tone: "warning" },
  { value: "Partially met", label: "Partially met, close the plan", tone: "warning" },
  { value: "Unsuccessful", label: "Unsuccessful, escalate to HR", tone: "danger" },
  { value: "Withdrawn", label: "Withdraw the plan", tone: "neutral" },
];

export const CALIBRATION_STATUS = {
  NOT_STARTED: "Not Started",
  IN_REVIEW: "In Review",
  LOCKED: "Locked",
};

export const CALIBRATION_GUIDE = [
  { rating: "Exceptional", target: 10 },
  { rating: "Exceeds Expectations", target: 20 },
  { rating: "Meets Expectations", target: 50 },
  { rating: "Needs Improvement", target: 15 },
  { rating: "Unsatisfactory", target: 5 },
];

export const RATING_BANDS = CALIBRATION_GUIDE.map((b) => b.rating);

export const POTENTIAL_LEVELS = ["Low", "Medium", "High"];

export const FEEDBACK_TYPES = {
  APPRECIATION: "Appreciation",
  IMPROVEMENT: "Improvement",
  GENERAL: "General",
};

export const FEEDBACK_VISIBILITY = [
  { value: "shared", label: "Visible to the person and their manager" },
  { value: "manager", label: "Visible to their manager only" },
  { value: "anonymous", label: "Anonymous, name hidden from the person" },
];

export const FEEDBACK_RELATIONS = ["Manager", "Peer", "Direct report", "Cross-functional", "Self"];

export const FEEDBACK_REQUEST_STATUS = {
  PENDING: "Pending",
  SUBMITTED: "Submitted",
  DECLINED: "Declined",
};

export const REMINDER_CHANNELS = ["Email", "In-app"];

export const REMINDER_FREQUENCIES = ["Daily", "Every 2 days", "Weekly", "Once"];

export const REMINDER_TASK_TYPES = {
  GOAL_SETTING: "Goal setting pending",
  GOAL_APPROVAL: "Goal approval pending",
  SELF_ASSESSMENT: "Self assessment pending",
  MANAGER_REVIEW: "Manager review pending",
  GOAL_PROGRESS: "Goal progress not updated",
  PIP_CHECKIN: "PIP check-in due",
  FEEDBACK_REQUEST: "Feedback request pending",
};

export const MAX_GOALS_PER_YEAR = 5;
export const goalLimitMessage = (yearLabel) =>
  `${MAX_GOALS_PER_YEAR} goals have already been added for the year${yearLabel ? ` ${yearLabel}` : ""}. You can't add a 6th goal. Remove or edit an existing goal instead.`;
